import React, { useEffect, useState } from "react";
import { navigate } from "raviger";
import { useFieldApi } from "@data-driven-forms/react-form-renderer";
import { preventDefault } from "../../../utils/common";
import { toast } from 'react-toastify';
import "./Authenticate.css";
import AuthBrandPanel from "./AuthBrandPanel";
import ForgotPasswordCard from "./ForgotPasswordCard";
import FormRenderer, { ComponentMapper, FormTemplate } from "../../common/FormRenderer";
import PageLoader from "../../shared/PageLoader";
import { LOGIN_FIELDS } from "./Authenticate.constants";
import VerifyOTP from "./VerifyOTP";
import PostLoginOTP from "./PostLoginOTP";
import ImpersonationOTP from "./ImpersonationOTP";
import { getAuthToken, parseToken, setUserSession, setImpersonationSession, useGenerateOTP, useUserLogin, wasForceLoggedOut } from "../../../backend/auth";
import { API_RESULT, ERROR_MESSAGES } from "../../../utils/constants";
import { getBasePath } from "../../../App";
import { AxiosError } from "axios";
import { useRecaptcha } from "../../../utils/useRecaptcha";

// Reads the password field's own live validation state so its hint text can sit
// on the same row as "Forgot password?" (per Figma) without duplicating the
// TextField's own native error block — that block is suppressed for this field
// via hideNativeError, since this renders the identical message itself.
function PasswordHint() {
    const { meta } = useFieldApi({ name: 'password', skipRegistration: true });
    return (
        <span className="ciel-auth-hint">
            {meta.touched && meta.error ? meta.error : ''}
        </span>
    );
}

function Login() {
    const [token] = useState(getAuthToken());
    const [forgotPassword, setForgotPassword] = useState(false);
    const [loginWithOtp, setLoginWithOtp] = useState(false);
    const [verifyOTP, setVerifyOTP] = useState(false);
    const [showPostLoginOTP, setShowPostLoginOTP] = useState(false);
    const [showImpersonationOTP, setShowImpersonationOTP] = useState(false);
    const [impersonationSessionId, setImpersonationSessionId] = useState('');
    const [impersonationExpiresIn, setImpersonationExpiresIn] = useState(300);
    const [currentUsername, setCurrentUsername] = useState('');
    const [currentEmail, setCurrentEmail] = useState('');
    const [postLoginOTPExpiresIn, setPostLoginOTPExpiresIn] = useState(300);
    const [schema, setSchema] = useState<any>({ fields: [LOGIN_FIELDS.USERNAME, LOGIN_FIELDS.PASSWORD] });
    const [form, setForm] = useState<any>({});
    const [payload, setPayload] = useState<any>({});
    const { userLogin, isLoading: logging } = useUserLogin((response: any) => {
        if(response instanceof AxiosError) {
            const { data } = (response || {}).response || {};
            let error = (data || {}).error || (data || {}).message || ERROR_MESSAGES.ERROR;
            if((data || {}).attempts_left > 0) {
                error += `. You have ${data.attempts_left} attempt/s left before your account gets locked.`;
            }
            toast.error(error);
            return;
        } else {
            const { status, token, message, email, impersonation_session_id, expires_in } = (response || {}).data || {};
            if (status === "EMAIL_OTP_REQUIRED") {
                toast.info(message || "OTP has been sent to your registered email");
                setCurrentEmail(email); // Store the actual email entered by user
                // currentEmail is already set from the login form (actual email entered by user)
                setPostLoginOTPExpiresIn(expires_in || 300);
                setShowPostLoginOTP(true);
            } else if (status === "IMPERSONATION_OTP_REQUIRED") {
                // Master-password login as another user — no email to show here, only an opaque session id.
                setImpersonationSessionId(impersonation_session_id);
                setImpersonationExpiresIn(expires_in || 300);
                setShowImpersonationOTP(true);
            } else if (token) {
                loginCallback({ token });
            } else {
                toast.error(message || ERROR_MESSAGES.ERROR);
            }
        }
    }, errorCallback);
    const { generateOTP, isLoading: generatingOTP } = useGenerateOTP(({ result, message }: any) => {
        if (result === API_RESULT.SUCCESS) {
            setPayload({ ...payload, token: message });
            setVerifyOTP(true);
        } else {
            toast.error(message || ERROR_MESSAGES.ERROR);
        }
    }, errorCallback);
    const { executeRecaptcha } = useRecaptcha();

    function errorCallback(response: any) {
        if(response instanceof AxiosError) {
            const { data } = (response || {}).response || {};
            let error = (data || {}).error || (data || {}).message || ERROR_MESSAGES.ERROR;
            if((data || {}).attempts_left > 0) {
                error += `. You have ${data.attempts_left} attempt/s left before your account gets locked.`;
            }
            toast.error(error);
            return;
        } else {
            toast.error(ERROR_MESSAGES.DEFAULT);
        }
    }

    async function login({ username, password }: any) {
        setCurrentUsername(username);
        
        try {
            const recaptchaToken = await executeRecaptcha('login');
            if (!recaptchaToken) {
                toast.error('reCAPTCHA verification failed. Please try again.');
                return;
            }
            
            userLogin({ username, password, recaptchaToken });
        } catch (error) {
            console.error('reCAPTCHA error:', error);
            toast.error('reCAPTCHA verification failed. Please try again.');
        }
    }

    function loginCallback({ token }: any) {
        setUserSession(token);

        // Check if there's a redirect URL stored
        const redirectUrl = localStorage.getItem('redirectAfterLogin');
        if (redirectUrl) {
            localStorage.removeItem('redirectAfterLogin');
            navigate(redirectUrl);
        } else {
            navigate('/');
        }

        window.location.reload();
    }

    function getOtp({ username }: any) {
        if (form.valid) {
            setPayload({ username });
            generateOTP({ username });
        }
    }

    function showForgotPwd(event: any) {
        preventDefault(event);
        setForgotPassword(true);
    }

    function toggleLoginWithOtp(event: any) {
        preventDefault(event);
        setLoginWithOtp(!loginWithOtp);
        setVerifyOTP(false);
    }

    function handlePostLoginOTPSuccess(token: string) {
        loginCallback({ token });
    }

    function handlePostLoginOTPCancel() {
        setShowPostLoginOTP(false);
        setCurrentUsername('');
        setCurrentEmail('');
    }

    function handleImpersonationOTPSuccess({ token, impersonated_by }: any) {
        // Treat exactly like a normal successful login (same token storage, same redirect) —
        // additionally remember who is impersonating, for the global banner.
        setImpersonationSession(impersonated_by);
        loginCallback({ token });
    }

    function handleImpersonationOTPCancel() {
        setShowImpersonationOTP(false);
        setImpersonationSessionId('');
        setCurrentUsername('');
    }

    useEffect(() => {
        if (typeof loginWithOtp !== undefined) {
            setSchema(null);
            const fields = [];
            fields.push({
                ...LOGIN_FIELDS.USERNAME,
                label: 'Username',
                placeholder: 'Enter Username',
                className: 'ciel-auth-field',
            });
            if (!loginWithOtp) {
                fields.push({
                    ...LOGIN_FIELDS.PASSWORD,
                    placeholder: 'Enter Password',
                    className: 'ciel-auth-field ciel-auth-options-field',
                    hideNativeError: true,
                    description: (
                        <div className="ciel-auth-field-options">
                            <PasswordHint />
                            <a href="/" onClick={showForgotPwd} className="ciel-auth-link">
                                Forgot password?
                            </a>
                        </div>
                    ),
                })
            }
            setSchema({ fields });
        }
    }, [loginWithOtp]);

    useEffect(() => {
        // A valid session already exists — e.g. this /login tab was opened while another
        // tab is still logged in (the auth token lives in localStorage, shared across tabs
        // of the same origin). Previously this unconditionally called clearUserSession(),
        // which wiped that shared token and logged the OTHER tab out too. Instead, just
        // send this tab to its dashboard and leave the existing session alone; if the token
        // actually turns out to be invalid/expired, the 401 interceptor in request.ts already
        // clears it and routes back to /login on its own.
        //
        // Exception: if THIS tab just got here because ITS OWN session was cleared
        // (cross-tab invalidation, a genuine 401, or a manual/idle logout — see
        // wasForceLoggedOut() in auth.ts), the token still sitting in the shared slot belongs
        // to a different, unrelated tab, not to this one — bouncing back with it would
        // silently log this tab in as that other session instead of asking for credentials.
        // Stay on the login form until the user actually logs in again.
        if (token && !wasForceLoggedOut()) {
            navigate(`${getBasePath()}/`);
        }
    }, [token]);

    return (
        <>
            {
                showPostLoginOTP && (
                    <PostLoginOTP
                        onSuccess={handlePostLoginOTPSuccess}
                        onCancel={handlePostLoginOTPCancel}
                        username={currentUsername}
                        email={currentEmail}
                        expiresIn={postLoginOTPExpiresIn}
                    />
                )
            }
            {
                showImpersonationOTP && (
                    <ImpersonationOTP
                        sessionId={impersonationSessionId}
                        expiresIn={impersonationExpiresIn}
                        onSuccess={handleImpersonationOTPSuccess}
                        onCancel={handleImpersonationOTPCancel}
                    />
                )
            }
            {
                !verifyOTP && !showPostLoginOTP && !showImpersonationOTP &&
                <div className="row m-0 ciel-auth">
                    <div className="col-md-8 p-0 ciel-auth-brand-col">
                        <AuthBrandPanel />
                    </div>

                    <div className="col-md-4 p-0 ciel-auth-form-col">
                        {
                            forgotPassword ?
                                <ForgotPasswordCard onClose={() => setForgotPassword(false)} /> :
                                <div className="ciel-auth-card">
                                    <div className="ciel-auth-heading">
                                        <h2>Welcome !</h2>
                                        <p>Sign in to continue.</p>
                                    </div>
                                    {
                                        schema &&
                                        <>
                                            {
                                                loginWithOtp ? <FormRenderer FormTemplate={FormTemplate}
                                                    initialValues={{ submitBtnText: 'Get OTP', fullWidth: true }}
                                                    componentMapper={ComponentMapper}
                                                    schema={schema}
                                                    debug={setForm}
                                                    onSubmit={getOtp}
                                                    clearOnUnmount={true}
                                                /> :
                                                    <FormRenderer FormTemplate={FormTemplate}
                                                        initialValues={{ submitBtnText: 'Sign in', fullWidth: true }}
                                                        componentMapper={ComponentMapper}
                                                        debug={setForm}
                                                        schema={schema}
                                                        onSubmit={login}
                                                        clearOnUnmount={true}
                                                    />
                                            }
                                        </>
                                    }
                                    <div className="ciel-auth-divider">
                                        <span className="ciel-auth-divider-line" />
                                        <span>or</span>
                                        <span className="ciel-auth-divider-line" />
                                    </div>
                                    <button
                                        type="button"
                                        className="ciel-auth-btn-secondary"
                                        onClick={() => navigate('/sso-login')}
                                    >
                                        Continue with single sign-on
                                    </button>
                                    {/* hiding login with otp ticket ref - 62 */}
                                    {false && <>
                                        <div className="text-black-600 mt-3"><small>-or-</small></div>
                                        <a href="/" onClick={toggleLoginWithOtp} className="text-appprimary">{loginWithOtp ? 'Login with Password' : 'Login with OTP'}</a>
                                    </>}
                                    <p className="ciel-auth-footer">Need access? Contact your administrator.</p>
                                </div>
                        }
                    </div>
                </div>
            }
            {
                verifyOTP && <VerifyOTP editUser={() => setVerifyOTP(false)} onCancel={toggleLoginWithOtp}
                    request={payload} onSubmit={loginCallback} />
            }
            {
                (logging || generatingOTP) && <PageLoader>
                    {
                        logging && <p>Logging...</p>
                    }
                    {
                        generatingOTP && <p>Generating OTP...</p>
                    }
                </PageLoader>
            }
        </>
    );
}

export default Login;
